import { useEffect } from 'react'
import { graphql } from '@apollo/client/react/hoc'
import { withRouter } from 'react-router-dom'
import { compose, fromRenderProps, withProps } from 'react-recompose'
import queryString from 'query-string'
import { allConditionsPass, getPath, not, pipe, pick } from '@anewgo/functions'
import { useMetaTags, useTitle } from '@anewgo/hooks'
import { graphqlProps, withClient, withCommunity } from '../../hocs'
import { StoreConsumer } from '../../store'
import { spinnerIf } from '../../components/helper'
import {
  COMMUNITY_LITE,
  SITEPLAN_LITE_BY_ID,
  SITEPLAN_LITE_BY_COMMUNITY_ID,
} from '../../graphql'
import { useCommunitySelection, usePageView } from '../../utils/hooks'
import InteractiveSiteplanWrapper from '../../components/InteractiveSiteplanWrapper/InteractiveSiteplanWrapper'
import * as reducers from '../../store/reducers'
import { CHOOSE_LOT_AND_PLAN } from '@anewgo/interactive-siteplan-core'

/**
 * this page will display a community's siteplan and allows for homesite
 * selection
 * The route param elevationId can be used to filter the display to show only
 * lots that are applicable (lot exclusion)
 */
const Siteplan = ({
  client,
  community,
  match,
  history,
  siteplan,
  subSiteplanId,
  dispatch,
}) => {
  const title = `Siteplan for ${match.params.communityName}`
  const tagline = client && `${title} presented by ${client.name}`
  useEffect(() => {
    dispatch(reducers.setTgtCommunities([community]))
  }, [])

  useTitle({ title })
  usePageView(title)
  useCommunitySelection(community)
  useTitle({ title })
  useMetaTags({ name: 'description', content: tagline })

  const onSubSiteplanClick = (subSiteplanName) => {
    const subSiteplan = siteplan.subSiteplans.find(
      (siteplan) => siteplan.name === subSiteplanName
    )
    history.push(
      `/client/${client.altName}/community/${match.params.communityName}/siteplan?sub=${subSiteplan.id}`
    )
  }

  const deselectSubSiteplan = () => {
    history.push(
      `/client/${client.altName}/community/${match.params.communityName}/siteplan`
    )
  }

  return (
    <InteractiveSiteplanWrapper
      backButtonProps={{
        displayButton: Boolean(+subSiteplanId),
        title: 'Show Master Siteplan',
        onBackToMasterSiteplan: () => deselectSubSiteplan(),
      }}
      siteplanId={+subSiteplanId}
      siteplanMode={CHOOSE_LOT_AND_PLAN}
      masterSiteplan={siteplan}
      onSubSiteplanSelect={onSubSiteplanClick}
    />
  )
}

const Wrapped = compose(
  withRouter,
  withClient,
  withCommunity(COMMUNITY_LITE),
  fromRenderProps(StoreConsumer, pick('dispatch')),
  withProps(
    pipe(
      getPath('location', 'search'),
      queryString.parse,
      getPath('sub'),
      (subSiteplanId) => ({ subSiteplanId })
    )
  ),

  graphql(SITEPLAN_LITE_BY_ID, {
    skip: ({ subSiteplanId }) => !subSiteplanId,
    options: ({ match, subSiteplanId }) => ({
      variables: {
        clientName: match.params.clientName,
        id: +subSiteplanId,
      },
    }),
    props: graphqlProps('siteplanById', {
      mapResultsToProps: (data) => ({
        siteplan: data,
      }),
    }),
  }),

  // This query additionally contains subSiteplans unlike SITEPLAN_LITE_BY_ID.
  // This is because we know SITEPLAN_LITE_BY_ID will only be used for a selected subSiteplan.
  graphql(SITEPLAN_LITE_BY_COMMUNITY_ID, {
    skip: ({ community, subSiteplanId }) => !community?.id || subSiteplanId,
    options: ({ match, community }) => ({
      variables: {
        clientName: match.params.clientName,
        communityId: community.id,
      },
    }),
    props: graphqlProps('activeSiteplanByCommunityId', {
      mapResultsToProps: (data) => ({
        siteplan: data,
      }),
    }),
  }),
  spinnerIf(
    not(
      allConditionsPass(
        getPath('client'),
        getPath('community'),
        not(getPath('loading'))
      )
    )
  )
)(Siteplan)

export default Wrapped
